import type { Conversation, ConversationQuery, ConversationResult } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Field,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lastrouter/ui';
import { cn, Download } from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadUrl } from '../api/client.js';
import { useApiQuery } from '../api/hooks.js';
import { useSession } from '../app/session.js';
import { EmptyState, ErrorState, KeyValue, LoadingBlock, PageHeader, SectionTitle, StatusBadge } from '../components/common.js';
import { formatDateTime, formatNumber } from '../lib/format.js';

type Filters = { from: string; to: string; user: string; channel: string; subject_id: string; feedback: string; status: string };
const EMPTY: Filters = { from: '', to: '', user: '', channel: '', subject_id: '', feedback: '', status: '' };

/** Campos vazios ficam fora; as datas vão como AAAA-MM-DD (o serviço inclui o dia inteiro em `to`). */
function toQuery(f: Filters): Partial<ConversationQuery> {
  return Object.fromEntries(Object.entries(f).filter(([, v]) => v)) as Partial<ConversationQuery>;
}

function FeedbackBadge({ c }: { c: Conversation }) {
  const { t } = useTranslation();
  const latest = c.feedback[0];
  if (!latest) return <span className="text-fg-muted">{t('conversations.noFeedback')}</span>;
  return latest.kind === 'useful' ? <StatusBadge tone="success">{t('conversations.useful')}</StatusBadge> : <StatusBadge tone="danger">{t('conversations.wrong')}</StatusBadge>;
}

/** Argumentos da ferramenta, um por linha, como a LLM mandou. */
function StepInput({ input }: { input: Record<string, unknown> }) {
  const entries = Object.entries(input);
  if (!entries.length) return <span className="text-fg-muted">-</span>;
  return (
    <code className="block whitespace-pre-wrap break-all font-mono text-[length:var(--lr-text-xs)]">
      {entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n')}
    </code>
  );
}

function filterText(t: (k: string, o?: Record<string, unknown>) => string, f: ConversationResult['filters'][number]): string {
  if ('range' in f) return `${f.field}: ${t('conversations.range', { from: f.range.from, to: f.range.to })}`;
  if ('condition' in f) return `${f.field} ${f.condition.op} ${f.condition.variable ? `$(${f.condition.variable})` : f.condition.value}`;
  if ('prefix' in f) return `${f.field}: ${f.prefix}*`;
  return `${f.field}: ${f.exclude ? `${t('conversations.except')} ` : ''}${f.values.join(', ')}`;
}

function ResultCard({ r }: { r: ConversationResult }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{r.measures.join(', ')}</span>
          {r.certified ? <StatusBadge tone="success">{t('conversations.certified')}</StatusBadge> : <StatusBadge tone="warning">{t('conversations.notCertified')}</StatusBadge>}
        </div>
        <KeyValue
          items={[
            { label: t('conversations.filters'), value: r.filters.length ? r.filters.map((f) => filterText(t, f)).join('; ') : t('conversations.noFilters') },
            { label: t('audit.subject'), value: `${r.subject_id} (${r.app})` },
            { label: t('audit.query'), value: `${r.query_id} v${r.query_version}` },
            { label: t('audit.rows'), value: formatNumber(r.row_count) },
            { label: t('conversations.executedAt'), value: formatDateTime(r.executed_at) },
            ...(r.caveats.length ? [{ label: t('conversations.caveats'), value: r.caveats.join(' ') }] : []),
            // O painel não tem tela de resultado: o replay roda no servidor, como a pessoa que perguntou.
            { label: t('conversations.replay'), value: <code className="font-mono text-[length:var(--lr-text-xs)]">lastrouter replay {r.result_id}</code> },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function Detail({ c, onClose }: { c: Conversation; onClose: () => void }) {
  const { t } = useTranslation();
  const results = useApiQuery('conversationResults', { query: { ids: c.result_ids.join(',') } }, { enabled: c.result_ids.length > 0 });
  const missing = c.result_ids.length - (results.data?.length ?? 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('conversations.detail')} description={`${c.user} · ${formatDateTime(c.ts)}`} closeLabel={t('common.close')} className="w-[min(860px,calc(100vw-32px))]">
        <div className="flex max-h-[75vh] flex-col gap-5 overflow-y-auto">
          <section>
            <SectionTitle>{t('conversations.question')}</SectionTitle>
            <p className="whitespace-pre-wrap text-fg">{c.question ?? <span className="text-fg-muted">{t('conversations.noQuestion')}</span>}</p>
          </section>
          <section>
            <SectionTitle>{t('conversations.answer')}</SectionTitle>
            {c.kind === 'audit' ? <Alert tone="info" title={t('conversations.auditOnly')} /> : <p className="whitespace-pre-wrap text-fg">{c.answer || '-'}</p>}
          </section>
          <KeyValue
            items={[
              { label: t('audit.channel'), value: c.channel },
              { label: t('audit.status'), value: c.status === 'error' ? `${t('audit.statuses.error')}${c.error ? ` (${c.error})` : ''}` : t('conversations.ok') },
              { label: t('conversations.model'), value: c.model_id ?? '-' },
              { label: t('conversations.rounds'), value: c.llm_rounds ?? '-' },
              {
                label: t('conversations.tokens'),
                value: c.tokens ? t('conversations.tokensValue', { input: formatNumber(c.tokens.input), cached: formatNumber(c.tokens.cached), output: formatNumber(c.tokens.output), calls: formatNumber(c.tokens.calls) }) : '-',
              },
              { label: t('conversations.unverified'), value: c.unverified_numbers ?? '-' },
              { label: t('audit.duration'), value: `${formatNumber(c.duration_ms)} ms` },
            ]}
          />
          <section>
            <SectionTitle>{t('conversations.steps')}</SectionTitle>
            {c.steps.length === 0 ? (
              <p className="text-fg-muted">{t('conversations.noSteps')}</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {c.steps.map((s, i) => (
                  <li key={i} className="rounded-[var(--lr-radius-md)] border border-border p-3">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="font-medium text-fg">
                        {i + 1}. {t(`channels.toolNames.${s.tool}`, { defaultValue: s.tool })}
                      </span>
                      <StatusBadge tone={s.status === 'ok' ? 'success' : 'danger'}>{s.status === 'ok' ? t('conversations.ok') : `${t('audit.statuses.error')}${s.error_code ? ` (${s.error_code})` : ''}`}</StatusBadge>
                      <span className="tabular text-fg-secondary">{formatNumber(s.ms)} ms</span>
                      {s.result_id ? <code className="font-mono text-[length:var(--lr-text-xs)] text-fg-secondary">{s.result_id}</code> : null}
                    </div>
                    {c.kind === 'turn' ? <StepInput input={s.input} /> : null}
                  </li>
                ))}
              </ol>
            )}
          </section>
          {c.result_ids.length ? (
            <section>
              <SectionTitle>{t('conversations.results')}</SectionTitle>
              {results.isPending ? (
                <LoadingBlock rows={2} />
              ) : results.error ? (
                <ErrorState error={results.error} onRetry={() => void results.refetch()} />
              ) : (
                <div className="flex flex-col gap-3">
                  {results.data.map((r) => (
                    <ResultCard key={r.result_id} r={r} />
                  ))}
                  {missing > 0 ? <p className="text-fg-muted">{t('conversations.resultsGone', { count: missing })}</p> : null}
                </div>
              )}
            </section>
          ) : null}
          <section>
            <SectionTitle>{t('conversations.feedback')}</SectionTitle>
            {c.feedback.length === 0 ? (
              <p className="text-fg-muted">{t('conversations.noFeedback')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {c.feedback.map((f) => (
                  <li key={f.id}>
                    <p className="flex flex-wrap gap-x-3">
                      <span className="font-medium text-fg">{t(`quality.feedback.kinds.${f.kind}`)}</span>
                      <span className="text-fg-secondary">{f.user}</span>
                      <span className="text-fg-secondary">{formatDateTime(f.ts)}</span>
                    </p>
                    {f.comment ? <p className="whitespace-pre-wrap text-fg">{f.comment}</p> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Conversas: o que perguntaram, como foi respondido e o retorno. Admin vê tudo; o dono, as conversas dos seus apps. */
export function ConversationsPage() {
  const { t } = useTranslation();
  const session = useSession().data;
  const [form, setForm] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [detail, setDetail] = useState<Conversation | null>(null);
  const subjects = useApiQuery('subjects', undefined, { enabled: Boolean(session?.is_admin) });
  const subjectIds = session?.is_admin ? (subjects.data ?? []).map((s) => s.id) : [...new Set((session?.owner_of ?? []).map((o) => o.subject_id))];
  const q = useApiQuery('conversations', { query: toQuery(applied) });

  function submit(e: FormEvent) {
    e.preventDefault();
    setApplied(form);
  }

  const [find, setFind] = useState('');
  // ponytail: procura só nas conversas já carregadas (até o limite da consulta); busca no servidor se o período ficar grande demais.
  const needle = find.trim().toLowerCase();
  const all = q.data?.items ?? [];
  const items = needle ? all.filter((c) => `${c.question ?? ''} ${c.answer ?? ''} ${c.user}`.toLowerCase().includes(needle)) : all;
  return (
    <>
      <PageHeader
        title={t('conversations.title')}
        subtitle={t('conversations.subtitle')}
        actions={
          session?.is_admin ? (
            <Button asChild>
              <a href={downloadUrl('conversationsExport', toQuery(applied))} download="conversas.csv">
                <Download />
                {t('audit.export')}
              </a>
            </Button>
          ) : null
        }
      />

      <Card className="mb-4">
        <CardContent>
          <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7" onSubmit={submit} role="search" aria-label={t('audit.filter')}>
            <Field id="conversa-de" label={t('audit.from')}>
              <Input type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
            </Field>
            <Field id="conversa-ate" label={t('audit.to')}>
              <Input type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </Field>
            <Field id="conversa-usuario" label={t('audit.user')}>
              <Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} />
            </Field>
            <Field id="conversa-canal" label={t('audit.channel')}>
              <Input value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} />
            </Field>
            <Field id="conversa-app" label={t('audit.subject')}>
              <NativeSelect value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                {subjectIds.map((id) => (
                  <option key={id} value={id}>
                    {subjects.data?.find((s) => s.id === id)?.nome ?? id}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field id="conversa-retorno" label={t('conversations.feedback')}>
              <NativeSelect value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                <option value="useful">{t('conversations.useful')}</option>
                <option value="wrong">{t('conversations.wrong')}</option>
                <option value="none">{t('conversations.noFeedback')}</option>
              </NativeSelect>
            </Field>
            <Field id="conversa-estado" label={t('audit.status')}>
              <NativeSelect value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                <option value="ok">{t('conversations.ok')}</option>
                <option value="error">{t('audit.statuses.error')}</option>
              </NativeSelect>
            </Field>
            <div className="flex gap-2 sm:col-span-2 lg:col-span-7">
              <Button type="submit" variant="primary">
                {t('audit.filter')}
              </Button>
              <Button
                onClick={() => {
                  setForm(EMPTY);
                  setApplied(EMPTY);
                }}
              >
                {t('audit.clear')}
              </Button>
              <span className="self-center text-[length:var(--lr-text-sm)] text-fg-secondary">{t('conversations.defaultPeriod')}</span>
            </div>
          </form>
        </CardContent>
      </Card>

      {q.isPending ? (
        <LoadingBlock rows={8} />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : all.length === 0 ? (
        <EmptyState>{t('conversations.empty')}</EmptyState>
      ) : (
        <div aria-busy={q.isPlaceholderData} className={cn('lr-fade flex flex-col gap-3', q.isPlaceholderData && 'lr-stale')}>
          <Field id="conversa-procurar" label={t('conversations.find')} className="max-w-md">
            <Input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder={t('conversations.findPlaceholder')} autoComplete="off" />
          </Field>
          {items.length === 0 ? <EmptyState>{t('conversations.noMatch', { q: find.trim() })}</EmptyState> : null}
          {items.length === 0 ? null : (
            <Table stickyHeader>
              <TableCaption>{t('conversations.title')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('audit.when')}</TableHead>
                  <TableHead>{t('audit.user')}</TableHead>
                  <TableHead>{t('audit.channel')}</TableHead>
                  <TableHead>{t('conversations.question')}</TableHead>
                  <TableHead>{t('conversations.answer')}</TableHead>
                  <TableHead>{t('conversations.feedback')}</TableHead>
                  <TableHead className="text-right">{t('audit.duration')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} className="hover:bg-sunken">
                    <TableCell className="tabular whitespace-nowrap">
                      <button type="button" className="text-left underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-focus" onClick={() => setDetail(c)}>
                        {formatDateTime(c.ts)}
                      </button>
                    </TableCell>
                    <TableCell>{c.user}</TableCell>
                    <TableCell>{c.channel}</TableCell>
                    <TableCell className="max-w-[280px] truncate" title={c.question ?? undefined}>
                      {c.status === 'error' ? <StatusBadge tone="danger">{t('audit.statuses.error')}</StatusBadge> : null} {c.question ?? '-'}
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate text-fg-secondary">{c.kind === 'audit' ? t('conversations.auditShort') : c.answer || '-'}</TableCell>
                    <TableCell>
                      <FeedbackBadge c={c} />
                    </TableCell>
                    <TableCell className="tabular text-right">{formatNumber(c.duration_ms)} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-[length:var(--lr-text-sm)] text-fg-secondary" aria-live="polite">
            {q.data.truncated ? t('conversations.truncated', { count: items.length }) : t('conversations.shown', { count: items.length })}
          </p>
        </div>
      )}
      {detail ? <Detail c={detail} onClose={() => setDetail(null)} /> : null}
    </>
  );
}
